import React, { FC, useState } from "react";

import { FileWithPath, useDropzone } from "react-dropzone";

import { BackdropWithSpinner } from "@/components/atoms";

import DropZone from "../components/DropZone/DropZone";
import { defaultUploadErrors, FileUploaderProps } from "../type";
import {
    compressFiles,
    defaultAllowedFileTypes,
    defaultMaxFileSize,
    defaultMaxNumberOfFiles,
    defaultMaxTotalSize,
    getAcceptedFilesTotalSize,
    getReceivedErrors,
    sizeLimitCalc,
} from "../utils";

type PendingFileUploaderProps = {
    documents: FileWithPath[];
    handleAddDocument: (file: FileWithPath) => void;
} & FileUploaderProps;

const PendingFileUploader: FC<PendingFileUploaderProps> = ({
    maxNumberOfFiles = defaultMaxNumberOfFiles,
    maxFileSize = defaultMaxFileSize,
    maxTotalSize = defaultMaxTotalSize(maxFileSize, maxNumberOfFiles),
    allowedFileTypes = defaultAllowedFileTypes,
    handleAddDocument,
    documents,
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState(defaultUploadErrors);

    const { getRootProps, getInputProps } = useDropzone({
        accept: allowedFileTypes,
        maxFiles: 0, // We deal with the number of files programmatically
        maxSize: maxFileSize * sizeLimitCalc,
        getFilesFromEvent: async (event) => {
            if (!("type" in event) || event.type !== "dragenter") setIsUploading(true);
            return await compressFiles(event);
        },
        onDrop: (acceptedFiles: FileWithPath[], fileRejections) => {
            let newErrors = { ...defaultUploadErrors, ...getReceivedErrors(fileRejections) };

            // 1. Get files to be uploaded >> ignore duplicated files
            const filesDuplicated = [];
            const files = acceptedFiles.filter(
                (ad) =>
                    !documents.find((d) => {
                        if (d.name === ad.name) {
                            filesDuplicated.push(d.name);
                            return true;
                        }
                        return false;
                    }),
            );

            if (files.length !== acceptedFiles.length) {
                newErrors = { ...newErrors, filesDuplicated };
            }

            if (files.length === 0) {
                setErrors(newErrors);
                setIsUploading(false);
                return;
            }
            newErrors = { ...defaultUploadErrors, filesDuplicated };

            // 2. Remove extra files
            const filesToBeUploaded = files.slice(0, maxNumberOfFiles - documents.length);

            if (files.length !== filesToBeUploaded.length) {
                newErrors = { ...newErrors, totalFilesExceeded: true };
            }

            // 3. Get allowed files to be uploaded
            let sizeCount = getAcceptedFilesTotalSize(documents);
            filesToBeUploaded.forEach((fileToBeUploaded) => {
                const realFileSize = fileToBeUploaded.size / sizeLimitCalc;
                const expectedGlobalSize = sizeCount + realFileSize;

                if (maxTotalSize !== 0 && expectedGlobalSize > maxTotalSize) {
                    newErrors = {
                        ...newErrors,
                        filesTotalSizeExceeded: [...newErrors.filesTotalSizeExceeded, fileToBeUploaded.name],
                    };
                } else {
                    handleAddDocument(fileToBeUploaded);
                    sizeCount += realFileSize;
                }
            });

            setErrors(newErrors);
            setIsUploading(false);
        },
    });

    const hasMaximumFiles = documents && documents.length > 0 && documents.length === maxNumberOfFiles;

    return (
        <>
            {isUploading && <BackdropWithSpinner />}
            <DropZone
                hasMaximumFiles={hasMaximumFiles}
                errors={errors}
                rootProps={getRootProps()}
                inputProps={getInputProps()}
            />
        </>
    );
};

export default PendingFileUploader;
